const dayOpened = [
    "sonntag_von",
    "montag_von",
    "dienstag_von",
    "mittwoch_von",
    "donnerstag_von",
    "freitag_von",
    "samstag_von"
]

const dayClosed = [
    "sonntag_bis",
    "montag_bis",
    "dienstag_bis",
    "mittwoch_bis",
    "donnerstag_bis",
    "freitag_bis",
    "samstag_bis"
]


function changeTimeFormat(time) {
    return time.split(":").slice(0, 2).join(":")
}

/**
 * Current wall-clock time in Berlin as "HH:MM:SS".
 *
 * Opening hours are stored as plain Berlin local times, so comparing them
 * against the visitor's own device clock made the shop look closed (or open)
 * for anyone outside Europe/Berlin. Always evaluate against Berlin.
 */
function berlinTimeNow() {
    try {
        return new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/Berlin",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }).format(new Date());
    } catch (e) {
        // Very old browsers without full ICU: fall back to device local time.
        return new Date().toTimeString().split(" ")[0];
    }
}

/**
 * Berlin weekday index matching JS Date.getDay() (0 = Sunday).
 */
function berlinWeekday() {
    try {
        const name = new Intl.DateTimeFormat("en-US", {
            timeZone: "Europe/Berlin",
            weekday: "short",
        }).format(new Date());
        const idx = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(name);
        return idx === -1 ? new Date().getDay() : idx;
    } catch (e) {
        return new Date().getDay();
    }
}

/**
 * @param {*} - partner data
 * @returns {boolean} - return boolean value if it is either opened (true) or closed (false)
 */
export function isOpened(data) {
    if (!data) return false;

    const currentDay = berlinWeekday();
    const opened = dayOpened[currentDay];
    const closed = dayClosed[currentDay];

    const openTime = data[opened];
    const closeTime = data[closed];

    // Missing/incomplete hours must never hide a restaurant or its products.
    if (!openTime || !closeTime) {
        return true;
    }

    // Explicit "always open".
    if (openTime === "00:00:00" && closeTime === "00:00:00") {
        return true;
    }

    const currentTime = berlinTimeNow();

    // Hours that cross midnight (e.g. 11:00-00:00, or 18:00-02:00) have a
    // closing time that is numerically <= the opening time. The open window is
    // then "openTime .. 23:59:59" PLUS "00:00:00 .. closeTime", so the test has
    // to be an OR. The previous AND-only version made such a day evaluate to
    // false at every single moment, which is why a restaurant with a
    // 11:00-00:00 day showed as CLOSED for that entire day, every week.
    if (closeTime <= openTime) {
        return currentTime >= openTime || currentTime <= closeTime;
    }

    return currentTime >= openTime && currentTime <= closeTime;
}


/**
 * @param {*} data
 *
 * @returns {*} - openTime and closeTime
 */

export function getTodayDates(data) {
    const currentDay = berlinWeekday();
    const opened = dayOpened[currentDay];
    const closed = dayClosed[currentDay];

    const openTime = data && data[opened];
    const closeTime = data && data[closed];

    // changeTimeFormat() calls .split() and would throw on a null/undefined
    // time, blanking the whole restaurant page.
    return {
        "open": openTime ? changeTimeFormat(openTime) : "",
        "close": closeTime ? changeTimeFormat(closeTime) : ""
    }
}

export function liefershops(endpoint) {
    const baseUrl = process.env.REACT_APP_URL || "/";
    return `${baseUrl}${endpoint}`;
}

// ---- Signed session (PLZ + selected partner) ------------------------------
//
// PLZ and the chosen partner are stored in a backend-signed cookie via
// /api/v1/session/. The frontend never writes the cookie directly, so the
// values can't be forged by the client; the backend validates the signature
// on every request that depends on them.

export async function loadSignedSession() {
    try {
        const resp = await fetch(liefershops('api/v1/session/'), { credentials: 'include' });
        if (resp.status === 204) return null;
        if (!resp.ok) return null;
        return await resp.json();
    } catch (e) {
        return null;
    }
}

export async function saveSignedSession({ plz, partner_slug, partner_id } = {}) {
    try {
        const resp = await fetch(liefershops('api/v1/session/'), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plz, partner_slug, partner_id }),
        });
        if (!resp.ok) return null;
        return await resp.json();
    } catch (e) {
        return null;
    }
}

export async function clearSignedSession() {
    try {
        await fetch(liefershops('api/v1/session/'), { method: 'DELETE', credentials: 'include' });
    } catch (e) {
        // ignore
    }
}

/**
 * Compose a display label for a product that prepends its Nummer.
 * Nummer can be alphanumeric ("3", "3a", "12 B"). If the name already starts
 * with "<digits>." (or "<digits><letter>.") the raw name is returned unchanged
 * so hand-curated "120. Pizza Margherita" style names keep working.
 */
export function formatProductDisplay(name, nummer) {
    const clean = (name == null ? '' : String(name)).trim();
    if (!clean) return '';
    if (/^\d+[a-zA-Zа-яА-Я]?\s*\.\s/.test(clean)) return clean;
    if (nummer !== null && nummer !== undefined && nummer !== '') {
        return `${String(nummer).trim()}. ${clean}`;
    }
    return clean;
}

/**
 * Natural-order comparator for alphanumeric Nummer values. Splits the input
 * into numeric and non-numeric chunks so that "2" < "10" < "10a" < "10b"
 * < "11". Works for plain ints stored as strings too.
 */
export function compareNummer(a, b) {
    const aStr = (a == null ? '' : String(a)).trim();
    const bStr = (b == null ? '' : String(b)).trim();
    if (!aStr && !bStr) return 0;
    if (!aStr) return 1;
    if (!bStr) return -1;
    return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
}

export async function createOrder(data, restaurant, discount) {
    const response = await fetch(liefershops('api/v1/paypal/create-order/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            total: data.preis_gesamt,
            products: data.products,
            lieferkosten: data.lieferkosten || 0,
            discount: discount || 0,
            restaurant_name: restaurant,
            return_url: window.location.origin + '/checkout',
            cancel_url: window.location.origin + '/checkout',
        }),
    });

    // Surface the backend's actual reason (missing credentials, no approval
    // link, PayPal rejection...) instead of a bare status code, so the
    // customer gets a meaningful message rather than a button that does
    // nothing at all.
    let result = null;
    try {
        result = await response.json();
    } catch (e) {
        result = null;
    }

    if (!response.ok) {
        const reason = (result && (result.error || result.detail)) || `HTTP ${response.status}`;
        throw new Error(reason);
    }

    if (!result || !result.approve_url) {
        throw new Error("PayPal hat keinen Zahlungslink zurückgegeben.");
    }

    return result.approve_url;
}

export async function capturePayment(orderId) {
    const response = await fetch(liefershops('api/v1/paypal/capture-order/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId }),
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
}
